import React, { useEffect, useMemo, useRef, useState } from 'react';
import css from './BlogMainPage.module.css';
import { LayoutSingleColumn, Page, PaginationLinks } from '../../components';
import Skeleton from '../../components/Skeleton/Skeleton';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { useIntl, FormattedMessage } from 'react-intl';
import BlogCard from './BlogCard/BlogCard';
import faqHelp from '../../assets/faq-help.png';
import IconCard from '../../components/SavedCardDetails/IconCard/IconCard';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getOwnListingsById } from './BlogMainPage.duck';
import { useHistory, useLocation } from 'react-router-dom';

const BlogMainPageComponent = (props) => {
    const {
        currentUser,
        listings = [],
        pagination,
        queryInProgress,
        queryListingsError,
        queryParams,
        scrollingDisabled,
        onManageDisableScrolling,
    } = props
    const intl = useIntl();
    const history = useHistory();
    const location = useLocation();
    const panelWidth = 62.5;
    // Render hints for responsive image
    const renderSizes = useMemo(() => [
        `(max-width: 767px) 100vw`,
        `(max-width: 1920px) ${panelWidth / 2}vw`,
        `${panelWidth / 3}vw`,
    ].join(', '), []);
    const [showMore, setShowMore] = useState(() => {
        const expand = queryParams && (queryParams.expand === true || queryParams.expand === 'true' || queryParams.expand === '1');
        return !!expand;
    });
    const page = Number(queryParams ? queryParams.page : 1);
    const currentLang = (queryParams && (queryParams.lang || queryParams.pub_blogTypePreference)) || 'en';
    const ENGLISH = 'en';
    const ARABIC = 'ar';
    const isArabic = currentLang === ARABIC;

    // Keep the `lang` query parameter in sync with the active UI locale
    useEffect(() => {
        const localeLanguage = intl.locale && typeof intl.locale === 'string'
            ? (intl.locale.toLowerCase().startsWith('ar') ? ARABIC : ENGLISH)
            : ENGLISH;

        // If URL already has the correct lang, do nothing
        const sp = new URLSearchParams(location.search || '');
        const current = sp.get('lang') || currentLang;
        if (current === localeLanguage) return;

        try {
            sp.set('lang', localeLanguage);
            sp.delete('page');
            history.push({ pathname: location.pathname, search: sp.toString() });
        } catch (e) {
            // fallback: hard navigate
            if (typeof window !== 'undefined') {
                const url = new URL(window.location.href);
                url.searchParams.set('lang', localeLanguage);
                url.searchParams.delete('page');
                window.location.assign(url.toString());
            }
        }
    }, [intl.locale, location.pathname, location.search, history, currentLang, ARABIC, ENGLISH]);

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const listingsAreLoaded = useMemo(() => !queryInProgress && hasPaginationInfo, [queryInProgress, hasPaginationInfo]);

    // When page query param changes (pagination), keep focus on blog list section.
    // Skip on initial mount so visiting Blog page stays at the top hero.
    const didMountRef = useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        if (typeof window === 'undefined') return;
        const el = document.getElementById('blog-list-section');
        if (el) {
            const top = el.getBoundingClientRect().top + window.pageYOffset - 80; // small offset under topbar
            window.scrollTo({ top, behavior: 'smooth' });
        }
    }, [page]);
    useEffect(() => {
        const expand = queryParams && (queryParams.expand === true || queryParams.expand === 'true' || queryParams.expand === '1');
        setShowMore(!!expand);
    }, [queryParams]);

    const topSection = useMemo(() => (
        listings.slice(0, 6)
    ), [listings]);

    const secondSection = useMemo(() => {
        if (!listings || showMore || page !== 1) return [];
        return listings.slice(6, 9);
    }, [listings, showMore, page]);

    const shouldShowExploreMore = useMemo(
        () => !showMore && page === 1 && listingsAreLoaded && pagination && pagination.totalPages > 1,
        [showMore, page, listingsAreLoaded, pagination]
    );
    const pageTitle=intl.formatMessage({id:'BlogMainPage.blogTitle'})
    return (
        <Page className={css.page} title={ pageTitle} scrollingDisabled={scrollingDisabled} referrer="origin">
            <LayoutSingleColumn
                mainColumnClassName={css.layoutWrapperMain}
                topbar={<TopbarContainer />}
                footer={<FooterContainer />}
            >
                <div className={css.mainWrapper}>
                    <div className={css.blogCoverSection}>
                        <div className={css.blogBadge}>
                            <span className={css.blogBadgeText}>
                                <FormattedMessage id="BlogPage.badge" defaultMessage="Resources /Blog" />
                            </span>
                        </div>
                        <h1 className={css.title}>
                            <FormattedMessage id="BlogPage.heading" defaultMessage="Unlock the Secrets of Language Learning" />
                        </h1>
                        <p className={css.subtitle}>
                            <FormattedMessage id="BlogPage.subheading" defaultMessage="Stay inspired with fresh tips, proven methods, and real experiences that help you reach your linguistic goals." />
                        </p>
                    </div>
                    <div className={css.blogCardContainer} id="blog-list-section">
                        <div className={css.blogList}>
                            {queryInProgress
                                ? Array.from({ length: 6 }).map((_, i) => (
                                    <div className={css.blogCard} key={`sk-${i}`}>
                                        <Skeleton width={'100%'} height={200} rounded={12} bottom={12} />
                                        <div style={{ padding: '0 8px' }}>
                                            <Skeleton width={'40%'} height={12} bottom={8} />
                                            <Skeleton width={'80%'} height={16} bottom={8} />
                                            <Skeleton width={'60%'} height={14} bottom={0} />
                                        </div>
                                    </div>
                                  ))
                                : topSection.map(l => (
                                    <BlogCard
                                        className={css.blogCard}
                                        key={l.id.uuid}
                                        listing={l}
                                        renderSizes={renderSizes}
                                    />
                                  ))}
                        </div>
                        {showMore && listingsAreLoaded && pagination && pagination.totalPages > 1 ? (
                            <div className={css.paginationWrapper}>
                                <PaginationLinks
                                    pageName="BlogMainPage"
                                    pageSearchParams={{ page, expand: true, lang: currentLang }}
                                    pagination={pagination}
                                />
                            </div>
                        ) : null}
                        <div className={css.arabicJourney}>
                            <div className={css.journeyTitle}>
                                <FormattedMessage id="BlogPage.arabicJourneyTitle" defaultMessage="Ready to Start Your Arabic Journey?" />
                            </div>
                            <div className={css.journeySubTitle}>
                                <FormattedMessage id="BlogPage.arabicJourneySubTitle" defaultMessage="Learn Arabic the fun, flexible, and rewarding way — with real tutors, live lessons, and a community that celebrates your progress every step of the way." />
                            </div>
                            <div className={css.journeyRow}>
                                <div className={css.journeyColumn}>
                                    <ul>
                                        <li>
                                            <FormattedMessage
                                                id="BlogPage.journey.item1"
                                                defaultMessage="<b>Live 1-on-1 Lessons</b> with verified native tutors."
                                                values={{ b: chunks => <span className={css.boldText}>{chunks}</span> }}
                                            />
                                        </li>
                                        <li>
                                            <FormattedMessage
                                                id="BlogPage.journey.item2"
                                                defaultMessage="<b>Learn Anytime, Anywhere</b> — from your laptop, tablet, or phone."
                                                values={{ b: chunks => <span className={css.boldText}>{chunks}</span> }}
                                            />
                                        </li>
                                        <li>
                                            <FormattedMessage
                                                id="BlogPage.journey.item3"
                                                defaultMessage="<b>Fun & Personalized Classes</b> tailored to your goals."
                                                values={{ b: chunks => <span className={css.boldText}>{chunks}</span> }}
                                            />
                                        </li>
                                        <li>
                                            <FormattedMessage
                                                id="BlogPage.journey.item4"
                                                defaultMessage="<b>Earn Rewards & Badges</b> as you learn and grow."
                                                values={{ b: chunks => <span className={css.boldText}>{chunks}</span> }}
                                            />
                                        </li>
                                    </ul>
                                    <a href='/s' className={css.findTutorButton}>
                                        <FormattedMessage id="BlogPage.journey.findTutor" defaultMessage="Find a Tutor" />
                                    </a>
                                </div>
                                <div className={css.journeyColumn}>
                                    <img src={faqHelp} alt="blog-image" />
                                </div>
                            </div>
                        </div>
                        {!showMore && secondSection.length > 0 ? (
                            <div className={css.blogList}>
                                {secondSection.map(l => (
                                    <BlogCard
                                        className={css.blogCard}
                                        key={l.id.uuid}
                                        listing={l}
                                        renderSizes={renderSizes}
                                    />
                                ))}
                            </div>
                        ) : null}

                        <div className={css.exploreMore}>
                            {shouldShowExploreMore ? (
                                <button
                                    type="button"
                                    className={css.exploreMoreButton}
                                    onClick={() => {
                                        setShowMore(true);
                                        if (typeof window !== 'undefined') {
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }
                                    }}
                                >
                                    <IconCard type="exploreplus" />
                                    <FormattedMessage id="BlogPage.exploreMore" defaultMessage="Explore More" />
                                </button>
                            ) : null}
                        </div>
                        
                    </div>
                    <div className={css.bottomCover}>
                        <div className={css.languageCover}>
                            <div className={css.languageTitle}>
                                <FormattedMessage id="RewardPage.cta.title" defaultMessage="Learn Arabic. Live the Language." />
                            </div>
                            <div className={css.languageText}>
                                <FormattedMessage id="RewardPage.cta.text" defaultMessage="Connect, grow, and earn rewards by learning consistently." />
                            </div>
                            <div className={css.blogJoinButton}>
                                <a href="/s">
                                    <FormattedMessage id="RewardPage.cta.join" defaultMessage="Join Us Today" />
                                </a>
                            </div>
                            <div className={css.flipAnimationLeft}>
                                <svg width="96" height="86" viewBox="0 0 96 86" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                        d="M89.2727 31.9133C82.5239 24.2146 72.9932 19.5121 62.7773 18.8404C52.5613 18.1687 42.497 21.5827 34.7982 28.3315C27.0995 35.0803 22.397 44.611 21.7253 54.8269C21.0535 65.0429 24.4676 75.1072 31.2164 82.806L60.2445 57.3597L89.2727 31.9133Z"
                                        fill="#FEC220" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </LayoutSingleColumn>
        </Page>
    )
}

const mapStateToProps = state => {
    const { currentUser } = state.user;
    const {
        currentPageResultIds,
        pagination,
        queryInProgress,
        queryListingsError,
        queryParams,
    } = state.BlogMainPage;
    const listings = getOwnListingsById(state, currentPageResultIds);
    return {
        currentUser,
        currentPageResultIds,
        listings,
        pagination,
        queryInProgress,
        queryListingsError,
        queryParams,
        scrollingDisabled: isScrollingDisabled(state),
    };
};

const mapDispatchToProps = dispatch => ({
    // 
});

const BlogMainPage = compose(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
)(BlogMainPageComponent);
export default BlogMainPage
